/**
 * LCA Calculator Service - Template Mode
 * 
 * Performs LCA calculations using pre-computed TRACI v2.1 scores
 * baked into the template JSON. No Brightway/Python dependency.
 * 
 * Sub-inventory: If an input has a `sub_inventory` field, additional per-unit
 * TRACI scores from the linked template are added on top of the base scores.
 * 
 * Formula: impact = Σ (quantity × (traci_score_per_unit + sub_inv_score)) per input
 */

import { getSubInventoryTraci } from './subInventoryResolver';
import { computeFUNormalizer } from './functionalUnit';
import { getActiveImpactSource, getImpactScores, getImpactLink, hasImpactScores } from './impactSource';

const TRACI_CATEGORIES = {
  acidification:                 { unit: 'kg SO2-eq',     label: 'Acidification' },
  climate_change:                { unit: 'kg CO2-eq',     label: 'Climate Change (GWP)' },
  ecotoxicity_freshwater:        { unit: 'CTUe',          label: 'Ecotoxicity, Freshwater' },
  eutrophication:                { unit: 'kg N-eq',       label: 'Eutrophication' },
  human_toxicity_carcinogenic:   { unit: 'CTUh',          label: 'Human Toxicity, Carcinogenic' },
  human_toxicity_non_carcinogenic: { unit: 'CTUh',        label: 'Human Toxicity, Non-carcinogenic' },
  ozone_depletion:               { unit: 'kg CFC-11-eq',  label: 'Ozone Depletion' },
  particulate_matter:            { unit: 'kg PM2.5-eq',   label: 'Particulate Matter' },
  photochemical_oxidant:         { unit: 'kg O3-eq',      label: 'Photochemical Oxidant Formation' },
};

const CATEGORY_KEYS = Object.keys(TRACI_CATEGORIES);

/**
 * Check if a project has baked-in TRACI scores (template mode)
 */
export function isTemplateMode(project) {
  if (!project || !project.processes) return false;
  const source = getActiveImpactSource(project);
  const processes = Object.values(project.processes);
  for (const proc of processes) {
    const inputs = proc.lci_inputs || [];
    for (const input of inputs) {
      if (hasImpactScores(input, source)) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Run LCA analysis using baked-in TRACI scores.
 * Returns results in the same format as the old Python lca_calculator.py
 * so ResultsPanel.js can display them without changes.
 */
export function runTemplateLCA(project, resolvedSubInventories = {}) {
  if (!project || !project.processes) {
    return { success: false, error: 'No project data' };
  }

  const processes = project.processes;
  const impactSource = getActiveImpactSource(project);
  const scalingFactor = project.project_info?.scaling_factor || 1.0;
  const lifeExtension = project.project_info?.battery_life_extension || 1.0;
  const norm = computeFUNormalizer(project);
  // Single finalization divisor: life extension (per service-life) × energy (per kWh).
  const finalDivisor = (lifeExtension > 1.0 ? lifeExtension : 1.0) * norm.divisor;

  // Per-process breakdown
  const processBreakdown = {};
  // Per-category totals
  const totals = {};
  CATEGORY_KEYS.forEach(k => { totals[k] = 0; });

  // Per-input detail for drill-down
  const inputDetails = {};

  for (const [procId, proc] of Object.entries(processes)) {
    const inputs = proc.lci_inputs || [];
    const procTotals = {};
    CATEGORY_KEYS.forEach(k => { procTotals[k] = 0; });

    const procInputDetails = [];

    for (const input of inputs) {
      const qty = (input.quantity || 0) * scalingFactor;
      // base_quantity: for sub-inventory inputs, base material was produced at original qty
      const baseQty = (input.base_quantity != null ? input.base_quantity : input.quantity || 0) * scalingFactor;
      const baseScores = getImpactScores(input, impactSource);
      const subInvScores = getSubInventoryTraci(input, resolvedSubInventories);

      const inputImpacts = {};
      CATEGORY_KEYS.forEach(k => {
        // Base impact scales with baseQty (oxide production), sub-inv scales with qty (coated powder)
        const impact = (baseQty * (baseScores[k] || 0)) + (qty * (subInvScores[k] || 0));
        inputImpacts[k] = impact;
        procTotals[k] += impact;
        totals[k] += impact;
      });

      const link = getImpactLink(input, impactSource);
      procInputDetails.push({
        name: input.name,
        quantity: qty,
        unit: input.unit,
        activity_name: link?.activity_name || input.activity_name || input.name,
        reference_product: link?.reference_product || input.reference_product || '',
        location: link?.location || input.location || '',
        impact_source: link?.source || null,
        impacts: inputImpacts,
      });
    }

    processBreakdown[procId] = {
      process_name: proc.name || procId,
      process_type: proc.process_type || '',
      manufacturing_category: proc.manufacturing_category || '',
      totals: procTotals,
      inputs: procInputDetails,
    };

    inputDetails[procId] = procInputDetails;
  }

  // Build method results in the format ResultsPanel expects
  // The old format: { method_name: { total, unit, process_breakdown: { procId: { total, inputs: [...] } } } }
  const methodResults = {};
  CATEGORY_KEYS.forEach(cat => {
    const meta = TRACI_CATEGORIES[cat];
    const methodKey = `TRACI v2.1 - ${meta.label}`;

    const procBreak = {};
    for (const [procId, pb] of Object.entries(processBreakdown)) {
      procBreak[procId] = {
        process_name: pb.process_name,
        total: pb.totals[cat],
        inputs: pb.inputs.map(inp => ({
          name: inp.name,
          quantity: inp.quantity,
          unit: inp.unit,
          activity_name: inp.activity_name,
          reference_product: inp.reference_product,
          location: inp.location,
          impact_source: inp.impact_source,
          impact: inp.impacts[cat],
        })),
      };
    }

    methodResults[methodKey] = {
      total: totals[cat],
      unit: norm.perKwh ? `${meta.unit} / kWh` : meta.unit,
      category_key: cat,
      process_breakdown: procBreak,
    };
  });

  // Apply the finalization divisor (life extension × energy normalization) to all
  // impacts. Life extension converts to per-unit-of-service-life; energy converts
  // to per-kWh. Same deep-divide that life extension used previously.
  if (finalDivisor !== 1.0) {
    CATEGORY_KEYS.forEach(k => { totals[k] /= finalDivisor; });
    for (const pb of Object.values(processBreakdown)) {
      CATEGORY_KEYS.forEach(k => { pb.totals[k] /= finalDivisor; });
      pb.inputs.forEach(inp => {
        CATEGORY_KEYS.forEach(k => { inp.impacts[k] /= finalDivisor; });
      });
    }
    // Rebuild methodResults with adjusted values
    CATEGORY_KEYS.forEach(cat => {
      const meta = TRACI_CATEGORIES[cat];
      const methodKey = `TRACI v2.1 - ${meta.label}`;
      methodResults[methodKey].total = totals[cat];
      for (const [procId, pb] of Object.entries(processBreakdown)) {
        methodResults[methodKey].process_breakdown[procId].total = pb.totals[cat];
        methodResults[methodKey].process_breakdown[procId].inputs.forEach((inp, i) => {
          inp.impact = pb.inputs[i].impacts[cat];
        });
      }
    });
  }

  return {
    success: true,
    method: 'TRACI v2.1',
    mode: 'template',
    impact_source: impactSource,
    scaling_factor: scalingFactor,
    battery_life_extension: lifeExtension,
    functional_unit_basis: norm.basis,
    energy_kwh: norm.perKwh ? norm.kwh : null,
    energy_density_wh_per_kg: norm.whPerKg ?? null,
    fu_fallback: norm.fallback || false,
    results: methodResults,
    totals: totals,
    category_metadata: TRACI_CATEGORIES,
  };
}

export { TRACI_CATEGORIES, CATEGORY_KEYS };
export default { isTemplateMode, runTemplateLCA, TRACI_CATEGORIES, CATEGORY_KEYS };
